import { DEFAULT_LANGUAGE } from '@avanta/shared/constants/common';
import { AppointmentService } from '@avanta/shared/services/appointments';
import { type Psychologist } from '@avanta/shared/types/psychologists';
import { extractTimeFromUnified, formatDateISO } from '@avanta/shared/utils/date';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { QUERY_KEYS } from 'constants/common';
import { AppointmentsReservePayload } from '../../../../types';

interface UseFetchTopPsychologistsReturn {
  psychologist: Psychologist | undefined;
  isLoading: boolean;
}

export const useFetchTopPsychologists = (): UseFetchTopPsychologistsReturn => {
  const { watch } = useFormContext();
  const [patientId, centerId, specialityId, dateTime, psychologistForm] = watch([
    AppointmentsReservePayload.patientId,
    AppointmentsReservePayload.backofficeCenterId,
    AppointmentsReservePayload.specialityBooked,
    AppointmentsReservePayload.dateTime,
    AppointmentsReservePayload.psychologist,
  ]);

  const selectedDate = dateTime ? formatDateISO(dateTime, "yyyy-MM-dd'T'HH:mm:ss") : '';
  const selectedTime = dateTime ? extractTimeFromUnified(dateTime) : '';

  const isCalendarQueryEnabled =
    !!specialityId && !!centerId && !!selectedDate && !!selectedTime && !!patientId;

  const {
    data: psychologist,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      QUERY_KEYS.FETCH_TOP_PSYCHOLOGISTS_AVAILABILITY,
      patientId,
      selectedDate,
      selectedTime,
      specialityId,
      centerId,
      DEFAULT_LANGUAGE,
    ],
    queryFn: () =>
      AppointmentService.getTopPsychologistsAvailability({
        startDate: selectedDate,
        days: 0,
        specialityId,
        centerId,
        patientId,
        spokenLanguage: DEFAULT_LANGUAGE,
        slotHour: selectedTime,
      }),
    select: response => response.data[0]?.psychologist,
    enabled: isCalendarQueryEnabled && !psychologistForm,
    placeholderData: keepPreviousData,
  });

  return {
    psychologist: psychologist,
    isLoading: isLoading || isFetching,
  };
};
